import React, { useEffect, useState } from 'react';
import {
  Card, Form, Input, InputNumber, Button, Space, Switch, Modal,
  message, Alert, Divider, Descriptions, Tag, Spin, Table, Popconfirm,
  Tooltip, Row, Col, Statistic, Typography, Select
} from 'antd';
import {
  MailOutlined, SaveOutlined, ReloadOutlined, PlusOutlined,
  DeleteOutlined, EditOutlined, SendOutlined, KeyOutlined,
  ThunderboltOutlined, CheckCircleOutlined, CloseCircleOutlined,
  SafetyOutlined
} from '@ant-design/icons';
import api, { get } from '../../utils/api';
import dayjs from 'dayjs';

const { Text, Paragraph } = Typography;
const MASKED = '***已设置***';

const GmailConfig = () => {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [globalConfig, setGlobalConfig] = useState({});
  const [accounts, setAccounts] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [testModalVisible, setTestModalVisible] = useState(false);
  const [testingId, setTestingId] = useState(null);
  const [auditRunning, setAuditRunning] = useState(false);
  const [whitelist, setWhitelist] = useState([]);
  const [whitelistInput, setWhitelistInput] = useState('');
  const [whitelistSaving, setWhitelistSaving] = useState(false);
  const [form] = Form.useForm();
  const [globalForm] = Form.useForm();

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statusRes, accountsRes, configRes, whitelistRes] = await Promise.all([
        get('/admin/gmail/status'),
        get('/admin/gmail/accounts'),
        get('/admin/gmail/config'),
        get('/admin/gmail/config/sender-whitelist')
      ]);
      if (statusRes.code === 200) setStatus(statusRes.data);
      if (accountsRes.code === 200) setAccounts(accountsRes.data?.accounts || []);
      if (configRes.code === 200) {
        const cfg = configRes.data || {};
        setGlobalConfig(cfg);
        globalForm.setFieldsValue(cfg);
      }
      if (whitelistRes.code === 200) setWhitelist(whitelistRes.data?.whitelist || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleSaveGlobal = async (values) => {
    try {
      const res = await api.put('/admin/gmail/config', { ...values });
      if (res.code === 200) { message.success('全局配置保存成功'); loadData(); }
    } catch (e) { console.error(e); }
  };

  const handleAddAccount = () => {
    setEditingAccount(null);
    form.resetFields();
    form.setFieldsValue({ isEnabled: true, lookbackMinutes: 0, autoAuditEnable: false, sortOrder: 0 });
    setModalVisible(true);
  };

  const handleEditAccount = (record) => {
    setEditingAccount(record);
    form.setFieldsValue({
      accountName: record.account_name,
      userEmail: record.user_email,
      appPassword: MASKED,
      isEnabled: !!record.is_enabled,
      lookbackMinutes: record.lookback_minutes || 0,
      autoAuditEnable: !!record.auto_audit_enable,
      sortOrder: record.sort_order || 0
    });
    setModalVisible(true);
  };

  const handleSaveAccount = async (values) => {
    const data = { ...values };
    if (editingAccount && data.appPassword === MASKED) delete data.appPassword;

    try {
      let res;
      if (editingAccount) {
        res = await api.put(`/admin/gmail/accounts/${editingAccount.id}`, data);
      } else {
        res = await api.post('/admin/gmail/accounts', data);
      }
      if (res.code === 200) {
        message.success(editingAccount ? '账户更新成功' : '账户创建成功');
        setModalVisible(false);
        loadData();
      }
    } catch (e) {
      message.error(e.response?.data?.message || '保存失败');
    }
  };

  const handleDeleteAccount = async (id) => {
    try {
      const res = await api.delete(`/admin/gmail/accounts/${id}`);
      if (res.code === 200) { message.success('删除成功'); loadData(); }
    } catch (e) { message.error('删除失败'); }
  };

  const handleTest = async (id) => {
    setTestingId(id);
    try {
      const res = await api.post(`/admin/gmail/accounts/${id}/test`);
      if (res.code === 200) { setTestResult(res.data); setTestModalVisible(true); }
    } catch (e) { console.error(e); }
    finally { setTestingId(null); }
  };

  const handleRunAudit = async () => {
    setAuditRunning(true);
    try {
      const res = await api.post('/admin/gmail/audit/run');
      if (res.code === 200) {
        const s = res.data;
        message.success(`执行完成：拉取 ${s.fetched}，处理 ${s.processed}，匹配 ${s.matched}，跳过 ${s.skipped}，失败 ${s.failed}`);
        loadData();
      }
    } catch (e) { console.error(e); }
    finally { setAuditRunning(false); }
  };

  const handleCronToggle = async (checked) => {
    try {
      const res = await api.post('/admin/gmail/cron/toggle', { isEnabled: checked });
      if (res.code === 200) { message.success(res.message); loadData(); }
    } catch (e) { console.error(e); loadData(); }
  };

  const handleAddWhitelistItem = () => {
    const val = whitelistInput.trim().toLowerCase();
    if (!val) return;
    if (!val.includes('@') || !val.includes('.')) {
      message.warning('请输入有效的邮箱地址或域名（如 @paypal.com）');
      return;
    }
    if (whitelist.includes(val)) {
      message.info('该地址已在白名单中');
      setWhitelistInput('');
      return;
    }
    setWhitelist([...whitelist, val]);
    setWhitelistInput('');
  };

  const handleRemoveWhitelistItem = (item) => {
    setWhitelist(whitelist.filter(e => e !== item));
  };

  const handleSaveWhitelist = async () => {
    setWhitelistSaving(true);
    try {
      const res = await api.put('/admin/gmail/config/sender-whitelist', { whitelist });
      if (res.code === 200) { message.success('发件人白名单保存成功'); loadData(); }
    } catch (e) { console.error(e); }
    finally { setWhitelistSaving(false); }
  };

  const accountColumns = [
    { title: '别名', dataIndex: 'account_name', width: 150 },
    { title: '邮箱', dataIndex: 'user_email', width: 200 },
    {
      title: '状态', dataIndex: 'is_enabled', width: 80,
      render: (v) => v ? <Tag color="green">启用</Tag> : <Tag>禁用</Tag>
    },
    {
      title: '回看', dataIndex: 'lookback_minutes', width: 100,
      render: (v) => v ? `${v}分钟` : <Text type="secondary">跟随全局</Text>
    },
    {
      title: '操作', key: 'action', width: 220,
      render: (_, record) => (
        <Space size="small">
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEditAccount(record)}>编辑</Button>
          <Button type="link" size="small" icon={<SendOutlined />} loading={testingId === record.id} onClick={() => handleTest(record.id)}>测试</Button>
          <Popconfirm title="确认删除该账户？" onConfirm={() => handleDeleteAccount(record.id)}>
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div>
      <Card title={<Space><MailOutlined /><span>Gmail 到账审核配置</span></Space>}
        extra={
          <Space>
            <Button icon={<ThunderboltOutlined />} onClick={handleRunAudit} loading={auditRunning}>手动执行一次审核</Button>
            <Button icon={<ReloadOutlined />} onClick={loadData}>刷新</Button>
          </Space>
        }
      >
        {/* 全局状态 */}
        {status && (
          <Alert
            message="服务状态"
            description={
              <Descriptions column={2} size="small">
                <Descriptions.Item label="全局开关">
                  {status.globalEnabled ? <Tag color="success" icon={<CheckCircleOutlined />}>已启用</Tag> : <Tag icon={<CloseCircleOutlined />}>已禁用</Tag>}
                </Descriptions.Item>
                <Descriptions.Item label="账户数">
                  <Text strong>{status.accounts?.length || 0}</Text> 个（{status.accounts?.filter(a => a.isEnabled).length || 0} 个启用）
                </Descriptions.Item>
                <Descriptions.Item label="定时任务">
                  {status.cronEnabled ? <Tag color="green" icon={<CheckCircleOutlined />}>已启用</Tag> : <Tag color="orange">未启用</Tag>}
                </Descriptions.Item>
                <Descriptions.Item label="回看窗口">{status.lookbackMinutes} 分钟</Descriptions.Item>
              </Descriptions>
            }
            type={status.globalEnabled && status.accounts?.some(a => a.isEnabled) ? 'success' : 'warning'}
            showIcon style={{ marginBottom: 24 }}
          />
        )}

        {/* 获取应用专用密码引导 */}
        <Alert
          message="获取应用专用密码"
          description={
            <div>
              <Paragraph style={{ marginBottom: 8 }}>应用专用密码不受 Google 应用验证限制，长期有效，无需每周重新授权。</Paragraph>
              <ol style={{ marginTop: 0, marginBottom: 8 }}>
                <li>开启两步验证：登录 <Text code>myaccount.google.com</Text> → 安全性 → 两步验证</li>
                <li>访问 <Text code>myaccount.google.com/apppasswords</Text> 生成 16 位应用专用密码</li>
                <li>在下方添加账户，填入邮箱与应用专用密码</li>
                <li>点击「测试」验证拉取，确认能解析出短码/金额</li>
                <li>开启「定时任务」，系统每分钟自动审核并匹配入账</li>
              </ol>
              <Paragraph type="warning" style={{ marginBottom: 0 }}>应用专用密码等同于邮箱完整访问凭证，请妥善保管。</Paragraph>
            </div>
          }
          type="info" showIcon icon={<KeyOutlined />} style={{ marginBottom: 24 }}
        />

        <Divider orientation="left">全局设置</Divider>
        <Spin spinning={loading}>
          <Form form={globalForm} layout="inline" onFinish={handleSaveGlobal} style={{ marginBottom: 24 }}>
            <Form.Item name="gmail_enable" label="全局开关" valuePropName="checked">
              <Switch checkedChildren="启用" unCheckedChildren="禁用" />
            </Form.Item>
            <Form.Item name="gmail_lookback_minutes" label="回看窗口(分钟)">
              <InputNumber min={5} max={1440} style={{ width: 100 }} />
            </Form.Item>
            <Form.Item name="gmail_auto_audit_enable" label="自动入账(全局默认)" valuePropName="checked">
              <Switch checkedChildren="启用" unCheckedChildren="禁用" />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" icon={<SaveOutlined />}>保存全局设置</Button>
            </Form.Item>
          </Form>
        </Spin>

        <Divider orientation="left">发件人白名单</Divider>
        <Spin spinning={loading}>
          <Alert
            message="白名单为空时不过滤所有邮件；添加邮箱地址或域名后，仅处理白名单中的发件人邮件"
            type="info" showIcon style={{ marginBottom: 12 }}
          />
          <Space wrap style={{ marginBottom: 12 }}>
            {whitelist.map(item => (
              <Tag
                key={item}
                closable
                onClose={() => handleRemoveWhitelistItem(item)}
                color="blue"
                style={{ fontSize: 13, padding: '2px 8px' }}
              >
                {item}
              </Tag>
            ))}
            {whitelist.length === 0 && <Text type="secondary">暂无白名单（不过滤）</Text>}
          </Space>
          <Space>
            <Input
              placeholder="输入邮箱或域名（如 pay@paypal.com 或 @paypal.com）"
              value={whitelistInput}
              onChange={(e) => setWhitelistInput(e.target.value)}
              onPressEnter={handleAddWhitelistItem}
              style={{ width: 380 }}
            />
            <Button icon={<PlusOutlined />} onClick={handleAddWhitelistItem}>添加</Button>
            <Button type="primary" icon={<SaveOutlined />} loading={whitelistSaving} onClick={handleSaveWhitelist}>保存白名单</Button>
          </Space>
        </Spin>

        <Divider orientation="left">邮箱账户</Divider>
        <Space style={{ marginBottom: 16 }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAddAccount}>添加账户</Button>
        </Space>
        <Table rowKey="id" columns={accountColumns} dataSource={accounts} pagination={false} size="small" loading={loading} />
      </Card>

      {/* 添加/编辑弹窗 */}
      <Modal
        title={editingAccount ? '编辑账户' : '添加账户'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        width={520}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSaveAccount}>
          <Form.Item name="accountName" label="账户别名" rules={[{ required: true, message: '请输入别名' }]}>
            <Input placeholder="如：收款邮箱A" />
          </Form.Item>
          <Form.Item name="userEmail" label="Gmail 邮箱" rules={[{ required: true, type: 'email', message: '请输入有效邮箱' }]}>
            <Input placeholder="toaccount@gmail.com" disabled={!!editingAccount} />
          </Form.Item>
          <Form.Item name="appPassword" label="应用专用密码"
            rules={[{
              validator: (_, v) => {
                if (v) return Promise.resolve();
                if (editingAccount) return Promise.resolve();
                return Promise.reject(new Error('请输入应用专用密码'));
              }
            }]}
          >
            <Input.Password placeholder={editingAccount ? '已设置时无需填写' : '16位应用专用密码'} />
          </Form.Item>
          <Form.Item name="isEnabled" label="启用" valuePropName="checked">
            <Switch checkedChildren="启用" unCheckedChildren="禁用" />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="lookbackMinutes" label="回看窗口(分钟)" extra="0=跟随全局">
                <InputNumber min={0} max={1440} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="autoAuditEnable" label="自动入账" extra="关闭=跟随全局" valuePropName="checked">
                <Switch checkedChildren="启用" unCheckedChildren="禁用" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="sortOrder" label="排序">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      {/* 测试结果弹窗 */}
      <Modal
        title="测试连接结果" open={testModalVisible} onCancel={() => setTestModalVisible(false)}
        footer={<Button onClick={() => setTestModalVisible(false)}>关闭</Button>} width={560}
      >
        {testResult && (
          <>
            <Statistic title="拉取到未读邮件" value={testResult.count} suffix="封" valueStyle={{ color: '#1890ff' }} style={{ marginBottom: 16 }} />
            {testResult.preview && !testResult.preview.error ? (
              <Descriptions column={1} size="small" bordered>
                <Descriptions.Item label="发件人">{testResult.preview.sender || '-'}</Descriptions.Item>
                <Descriptions.Item label="主题">{testResult.preview.subject || '-'}</Descriptions.Item>
                <Descriptions.Item label="接收时间">{testResult.preview.receivedAt ? dayjs(testResult.preview.receivedAt).format('YYYY-MM-DD HH:mm:ss') : '-'}</Descriptions.Item>
                <Descriptions.Item label="验证短码">{testResult.preview.shortCode ? <Tag color="green">{testResult.preview.shortCode}</Tag> : <Tag color="orange">未识别</Tag>}</Descriptions.Item>
                <Descriptions.Item label="金额">{testResult.preview.amount != null ? `${testResult.preview.amount} ${testResult.preview.currencyCode || ''}`.trim() : <Tag color="orange">未识别</Tag>}</Descriptions.Item>
              </Descriptions>
            ) : <Alert type="warning" message={testResult.preview?.error || '无邮件可预览'} showIcon />}
          </>
        )}
      </Modal>
    </div>
  );
};

export default GmailConfig;
